import React, { useEffect, useState } from "react";
import { Form, Input, Button, Switch, Typography, Select, Divider, message, Card, Row, Col, Space, Modal, DatePicker } from "antd";
import { useSelector } from "react-redux";
import * as authActions from "store/actions/AuthActions";
import { LogoutOutlined, ExclamationCircleOutlined, CalendarOutlined, FileTextOutlined, BankOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CKEditor } from "ckeditor4-react";
import { compactToolbar } from "utilities/config";

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

// CKEditor configuration for policies (basic toolbar)
const policyEditorConfig = {
    removePlugins: ["exportpdf", "forms", "smiley", "language", "iframe", "about", "image", "table"],
    toolbar: [
        { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike'] },
        { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
        { name: 'links', items: ['Link', 'Unlink'] },
        { name: 'tools', items: ['Undo', 'Redo'] }
    ],
    contentsCss: [process.env.REACT_APP_BASE_URL + "/staticfiles/ckeditor/4.19.0/full-all/document-style.css", "/ckeditor-content.css"],
    height: "200px",
    allowedContent: true,
  // Phones get a short toolbar; desktop keeps the full grouped one.
  ...compactToolbar(),
};

const Settings = () => {
    const [form] = Form.useForm(null);
    const [roleForm] = Form.useForm(null);
    const [payrollForm] = Form.useForm(null);
    const [policyForm] = Form.useForm(null);
    const [termsForm] = Form.useForm(null);
    const [accountingForm] = Form.useForm(null);
    const user = useSelector((state) => state.auth.user);
    const organization = useSelector((state) => state.auth.organization);
    const user_setting = useSelector((state) => state.userReducer?.user_setting);
    const [useUserEmail] = useState(user_setting ? user_setting.use_user_email : false)

    // State for roles and organization settings
    const [roles, setRoles] = useState([]);
    const [organizationSetting, setOrganizationSetting] = useState(null);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [savingRoles, setSavingRoles] = useState(false);
    const [applyingRole, setApplyingRole] = useState(false);
    const [loggingOutAllUsers, setLoggingOutAllUsers] = useState(false);
    const [savingPayroll, setSavingPayroll] = useState(false);
    const [savingTerms, setSavingTerms] = useState(false);
    const [savingAccounting, setSavingAccounting] = useState(false);

    // State for policies
    const [, setInvoicePolicy] = useState(null);
    const [, setQuotePolicy] = useState(null);
    const [, setLoadingPolicies] = useState(true);

    useEffect(() => {
        if (user_setting) {
            form.setFieldsValue({
                use_user_email: user_setting?.use_user_email,
                host: user_setting?.host,
                port: user_setting?.port,
                username: user_setting?.username,
                password: user_setting?.password,
            })
        }
    }, [user_setting])

    // Load roles and organization settings on mount
    useEffect(() => {
        loadRolesAndSettings();
    }, [user, organization]);

    // Load policies on mount
    useEffect(() => {
        loadPolicies();
    }, []);

    // Update form whenever organizationSetting changes
    useEffect(() => {
        if (organizationSetting) {
            const formValues = {
                default_employee_role: organizationSetting.default_employee_role?.id || null,
                default_hr_role: organizationSetting.default_hr_role?.id || null,
                default_accounts_role: organizationSetting.default_accounts_role?.id || null,
                default_admin_role: organizationSetting.default_admin_role?.id || null,
                default_sales_role: organizationSetting.default_sales_role?.id || null,
            };
            console.log("useEffect updating form with values:", formValues);
            roleForm.setFieldsValue(formValues);

            // Update payroll form (including FY settings)
            payrollForm.setFieldsValue({
                payroll_start_day: organizationSetting.payroll_start_day || 1,
                fy_start_month: organizationSetting.fy_start_month || 4,
                fy_start_day: organizationSetting.fy_start_day || 1,
            });

            // Update terms form
            termsForm.setFieldsValue({
                invoice_terms: organizationSetting.invoice_terms || '',
                quote_terms: organizationSetting.quote_terms || '',
            });

            // Update accounting form
            accountingForm.setFieldsValue({
                bank_transactions_locked_date: organizationSetting.bank_transactions_locked_date
                    ? dayjs(organizationSetting.bank_transactions_locked_date)
                    : null,
                exchange_rate_source: organizationSetting.exchange_rate_source || 'RBI',
            });
        }
    }, [organizationSetting, roleForm, payrollForm, termsForm, accountingForm]);

    const loadRolesAndSettings = async () => {
        try {
            setLoadingRoles(true);

            // Load all roles
            const rolesData = await authActions.loadRoles();
            setRoles(rolesData.results || rolesData || []);

            // Load organization settings if organization exists
            if (organization) {
                try {
                    const settingsData = await authActions.loadOrganizationSettings(organization);
                    console.log("Loaded organization settings:", settingsData);

                    // Handle different response formats
                    let setting = null;
                    if (settingsData.results && settingsData.results.length > 0) {
                        setting = settingsData.results[0];
                    } else if (Array.isArray(settingsData) && settingsData.length > 0) {
                        setting = settingsData[0];
                    } else if (settingsData && !Array.isArray(settingsData) && settingsData.id) {
                        // Direct object response
                        setting = settingsData;
                    }

                    if (setting) {
                        console.log("Setting organization setting:", setting);
                        setOrganizationSetting(setting);

                        const formValues = {
                            default_employee_role: setting.default_employee_role?.id || null,
                            default_hr_role: setting.default_hr_role?.id || null,
                            default_accounts_role: setting.default_accounts_role?.id || null,
                            default_admin_role: setting.default_admin_role?.id || null,
                            default_sales_role: setting.default_sales_role?.id || null,
                        };

                        console.log("Setting form values:", formValues);
                        roleForm.setFieldsValue(formValues);
                    } else {
                        console.log("No organization settings found");
                        setOrganizationSetting(null);
                        roleForm.resetFields();
                    }
                } catch (err) {
                    console.error("Error loading organization settings:", err);
                    setOrganizationSetting(null);
                }
            }
        } catch (error) {
            console.error("Error loading roles and settings:", error);
            message.error("Failed to load roles and settings");
        } finally {
            setLoadingRoles(false);
        }
    };

    const loadPolicies = async () => {
        try {
            setLoadingPolicies(true);
            const policiesData = await authActions.loadPolicies();

            if (policiesData) {
                // Handle both paginated and array responses
                const policies = policiesData.results || policiesData || [];

                // Find invoice and quote policies
                const invoice = policies.find(p => p.type === 'INVOICE');
                const quote = policies.find(p => p.type === 'QUOTE');

                setInvoicePolicy(invoice || null);
                setQuotePolicy(quote || null);

                // Set form values
                policyForm.setFieldsValue({
                    invoice_policy: invoice?.policy || '',
                    quote_policy: quote?.policy || '',
                });
            }
        } catch (error) {
            console.error("Error loading policies:", error);
        } finally {
            setLoadingPolicies(false);
        }
    };


    const handleSetting = (values) => {
        if (user_setting) {
            // dispatch(userActions.updateSetting({ ...values, id: user_setting?.id }))
        } else {
            // dispatch(userActions.createSetting({ ...values, user: user.id }))
        }
    };

    const handleRoleSettings = async (values) => {
        try {
            setSavingRoles(true);

            if (organizationSetting) {
                // Update existing setting - don't include organization field
                const updatePayload = {
                    default_employee_role: values.default_employee_role || null,
                    default_hr_role: values.default_hr_role || null,
                    default_accounts_role: values.default_accounts_role || null,
                    default_admin_role: values.default_admin_role || null,
                    default_sales_role: values.default_sales_role || null,
                };

                console.log("Updating organization setting with payload:", updatePayload);
                const updateResponse = await authActions.updateOrganizationSetting(organizationSetting.id, updatePayload);
                console.log("Update response:", updateResponse);

                // Reload settings to get the full updated data with nested role objects
                await loadRolesAndSettings();

                message.success("Default role settings updated successfully");
            } else {
                // Create new setting - include organization field
                const createPayload = {
                    organization: organization,
                    default_employee_role: values.default_employee_role || null,
                    default_hr_role: values.default_hr_role || null,
                    default_accounts_role: values.default_accounts_role || null,
                    default_admin_role: values.default_admin_role || null,
                    default_sales_role: values.default_sales_role || null,
                };

                try {
                    const newSetting = await authActions.createOrganizationSetting(createPayload);
                    setOrganizationSetting(newSetting);
                    message.success("Default role settings created successfully");
                } catch (createError) {
                    // Check if error is "already exists"
                    const errorMessage = createError?.response?.data?.organization?.[0] ||
                                       createError?.message ||
                                       String(createError);

                    if (errorMessage.includes("already exists")) {
                        // Setting exists but wasn't loaded, reload it and update directly
                        console.log("Setting already exists, reloading and updating...");

                        const settingsData = await authActions.loadOrganizationSettings(organization);
                        let existingSetting = null;

                        if (settingsData.results && settingsData.results.length > 0) {
                            existingSetting = settingsData.results[0];
                        } else if (Array.isArray(settingsData) && settingsData.length > 0) {
                            existingSetting = settingsData[0];
                        } else if (settingsData && !Array.isArray(settingsData) && settingsData.id) {
                            existingSetting = settingsData;
                        }

                        if (existingSetting) {
                            setOrganizationSetting(existingSetting);

                            // Update with payload that doesn't include organization
                            const updatePayload = {
                                default_employee_role: values.default_employee_role || null,
                                default_hr_role: values.default_hr_role || null,
                                default_accounts_role: values.default_accounts_role || null,
                                default_admin_role: values.default_admin_role || null,
                                default_sales_role: values.default_sales_role || null,
                            };

                            await authActions.updateOrganizationSetting(existingSetting.id, updatePayload);
                            message.success("Default role settings updated successfully");

                            // Reload settings again after update
                            await loadRolesAndSettings();
                        } else {
                            message.error("Could not load existing organization settings. Please refresh the page and try again.");
                        }
                    } else {
                        throw createError;
                    }
                }

                // Reload settings for newly created setting
                await loadRolesAndSettings();
            }
        } catch (error) {
            console.error("Error saving role settings:", error);
            const errorMsg = error?.response?.data?.message ||
                           error?.message ||
                           "Failed to save role settings";
            message.error(errorMsg);
        } finally {
            setSavingRoles(false);
        }
    };

    const handleApplyDefaultEmployeeRole = async () => {
        if (!organizationSetting || !organizationSetting.default_employee_role) {
            message.warning("Please select and save a default employee role first");
            return;
        }

        try {
            setApplyingRole(true);
            const result = await authActions.applyDefaultEmployeeRole(organizationSetting.id);
            message.success(result.message);
        } catch (error) {
            console.error("Error applying default employee role:", error);
            message.error("Failed to apply default employee role to all employees");
        } finally {
            setApplyingRole(false);
        }
    };

    const handleLogoutAllUsers = () => {
        confirm({
            title: 'Logout All Users',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>Are you sure you want to logout all non-admin users?</p>
                    <p style={{ marginTop: '12px', color: '#ff4d4f', fontWeight: 500 }}>
                        This will terminate all active sessions for regular users. Admin users will remain logged in.
                    </p>
                </div>
            ),
            okText: 'Yes, Logout All Users',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    setLoggingOutAllUsers(true);
                    const result = await authActions.logoutAllUsers();
                    message.success(result.message);
                } catch (error) {
                    console.error("Error logging out all users:", error);
                    const errorMsg = error?.response?.data?.error ||
                                   error?.message ||
                                   "Failed to logout all users";
                    message.error(errorMsg);
                } finally {
                    setLoggingOutAllUsers(false);
                }
            },
        });
    };

    const handlePayrollSettings = async (values) => {
        try {
            setSavingPayroll(true);

            if (organizationSetting) {
                // Update existing setting (including FY fields)
                const updatePayload = {
                    payroll_start_day: values.payroll_start_day,
                    fy_start_month: values.fy_start_month,
                    fy_start_day: values.fy_start_day,
                };

                await authActions.updateOrganizationSetting(organizationSetting.id, updatePayload);
                message.success("Settings updated successfully");
                await loadRolesAndSettings();
            } else {
                // Create new setting with all fields
                const createPayload = {
                    organization: organization,
                    payroll_start_day: values.payroll_start_day,
                    fy_start_month: values.fy_start_month,
                    fy_start_day: values.fy_start_day,
                };

                try {
                    const newSetting = await authActions.createOrganizationSetting(createPayload);
                    setOrganizationSetting(newSetting);
                    message.success("Settings created successfully");
                } catch (createError) {
                    const errorMessage = createError?.response?.data?.organization?.[0] ||
                                       createError?.message ||
                                       String(createError);

                    if (errorMessage.includes("already exists")) {
                        // Setting exists, reload and update
                        const settingsData = await authActions.loadOrganizationSettings(organization);
                        let existingSetting = null;

                        if (settingsData.results && settingsData.results.length > 0) {
                            existingSetting = settingsData.results[0];
                        } else if (Array.isArray(settingsData) && settingsData.length > 0) {
                            existingSetting = settingsData[0];
                        } else if (settingsData && !Array.isArray(settingsData) && settingsData.id) {
                            existingSetting = settingsData;
                        }

                        if (existingSetting) {
                            setOrganizationSetting(existingSetting);
                            await authActions.updateOrganizationSetting(existingSetting.id, {
                                payroll_start_day: values.payroll_start_day,
                                fy_start_month: values.fy_start_month,
                                fy_start_day: values.fy_start_day,
                            });
                            message.success("Settings updated successfully");
                            await loadRolesAndSettings();
                        }
                    } else {
                        throw createError;
                    }
                }
            }
        } catch (error) {
            console.error("Error saving settings:", error);
            message.error("Failed to save settings");
        } finally {
            setSavingPayroll(false);
        }
    };

    const handleTermsSettings = async (values) => {
        try {
            setSavingTerms(true);

            if (organizationSetting) {
                // Update existing setting
                const updatePayload = {
                    invoice_terms: values.invoice_terms || '',
                    quote_terms: values.quote_terms || '',
                };

                await authActions.updateOrganizationSetting(organizationSetting.id, updatePayload);
                message.success("Default terms updated successfully");
                await loadRolesAndSettings();
            } else {
                // Create new setting with all fields
                const createPayload = {
                    organization: organization,
                    invoice_terms: values.invoice_terms || '',
                    quote_terms: values.quote_terms || '',
                };

                try {
                    const newSetting = await authActions.createOrganizationSetting(createPayload);
                    setOrganizationSetting(newSetting);
                    message.success("Default terms created successfully");
                } catch (createError) {
                    const errorMessage = createError?.response?.data?.organization?.[0] ||
                                       createError?.message ||
                                       String(createError);

                    if (errorMessage.includes("already exists")) {
                        // Setting exists, reload and update
                        const settingsData = await authActions.loadOrganizationSettings(organization);
                        let existingSetting = null;

                        if (settingsData.results && settingsData.results.length > 0) {
                            existingSetting = settingsData.results[0];
                        } else if (Array.isArray(settingsData) && settingsData.length > 0) {
                            existingSetting = settingsData[0];
                        } else if (settingsData && !Array.isArray(settingsData) && settingsData.id) {
                            existingSetting = settingsData;
                        }

                        if (existingSetting) {
                            setOrganizationSetting(existingSetting);
                            await authActions.updateOrganizationSetting(existingSetting.id, {
                                invoice_terms: values.invoice_terms || '',
                                quote_terms: values.quote_terms || '',
                            });
                            message.success("Default terms updated successfully");
                            await loadRolesAndSettings();
                        }
                    } else {
                        throw createError;
                    }
                }
            }
        } catch (error) {
            console.error("Error saving terms settings:", error);
            message.error("Failed to save default terms");
        } finally {
            setSavingTerms(false);
        }
    };

    const handleAccountingSettings = async (values) => {
        try {
            setSavingAccounting(true);

            const lockedDate = values.bank_transactions_locked_date
                ? values.bank_transactions_locked_date.format('YYYY-MM-DD')
                : null;

            if (organizationSetting) {
                // Update existing setting
                const updatePayload = {
                    bank_transactions_locked_date: lockedDate,
                    exchange_rate_source: values.exchange_rate_source,
                };

                await authActions.updateOrganizationSetting(organizationSetting.id, updatePayload);
                message.success("Accounting settings updated successfully");
                await loadRolesAndSettings();
            } else {
                // Create new setting with all fields
                const createPayload = {
                    organization: organization,
                    bank_transactions_locked_date: lockedDate,
                    exchange_rate_source: values.exchange_rate_source,
                };

                try {
                    const newSetting = await authActions.createOrganizationSetting(createPayload);
                    setOrganizationSetting(newSetting);
                    message.success("Accounting settings created successfully");
                } catch (createError) {
                    const errorMessage = createError?.response?.data?.organization?.[0] ||
                                       createError?.message ||
                                       String(createError);

                    if (errorMessage.includes("already exists")) {
                        // Setting exists, reload and update
                        const settingsData = await authActions.loadOrganizationSettings(organization);
                        let existingSetting = null;

                        if (settingsData.results && settingsData.results.length > 0) {
                            existingSetting = settingsData.results[0];
                        } else if (Array.isArray(settingsData) && settingsData.length > 0) {
                            existingSetting = settingsData[0];
                        } else if (settingsData && !Array.isArray(settingsData) && settingsData.id) {
                            existingSetting = settingsData;
                        }

                        if (existingSetting) {
                            setOrganizationSetting(existingSetting);
                            await authActions.updateOrganizationSetting(existingSetting.id, {
                                bank_transactions_locked_date: lockedDate,
                                exchange_rate_source: values.exchange_rate_source,
                            });
                            message.success("Accounting settings updated successfully");
                            await loadRolesAndSettings();
                        }
                    } else {
                        throw createError;
                    }
                }
            }
        } catch (error) {
            console.error("Error saving accounting settings:", error);
            message.error("Failed to save accounting settings");
        } finally {
            setSavingAccounting(false);
        }
    };

    // Calculate payroll end day based on start day
    const getPayrollEndDay = (startDay) => {
        if (!startDay) return '-';
        // End day is start day - 1 of next month
        // If start day is 1, end day is last day of month (show as "End of month")
        // If start day is 15, end day is 14
        if (startDay === 1) {
            return "End of month";
        }
        return `${startDay - 1}`;
    };

    // Calculate FY end date based on start month and day
    const getFYEndDate = (startMonth, startDay) => {
        if (!startMonth) return '-';
        const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December'];

        // FY end is one day before start of next FY
        if (startDay === 1) {
            // End on last day of previous month
            const endMonth = startMonth === 1 ? 12 : startMonth - 1;
            // Get last day of the end month (approximate for display)
            const lastDays = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
            return `${lastDays[endMonth]} ${monthNames[endMonth]}`;
        } else {
            // End on (startDay - 1) of startMonth
            return `${startDay - 1} ${monthNames[startMonth]}`;
        }
    };

    return (
        <div style={{
            padding: '24px',
            height: '100%',
            overflowY: 'auto',
            backgroundColor: '#f0f2f5'
        }}>
            <Row gutter={[24, 24]}>
                {/* SMTP Settings Card */}
                <Col xs={24} lg={12}>
                    <Card
                        title={<Title level={4} style={{ margin: 0 }}>SMTP Information</Title>}
                        bordered={false}
                        style={{ height: '100%' }}
                    >
                        <Form
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 16 }}
                            layout="horizontal"
                            initialValues={user_setting}
                            onFinish={handleSetting}
                            form={form}
                        >
                            <Form.Item label="Host" name="host">
                                <Input disabled={useUserEmail} />
                            </Form.Item>
                            <Form.Item label="Port" name="port">
                                <Input disabled={useUserEmail} />
                            </Form.Item>
                            <Form.Item label="Username" name="username">
                                <Input disabled={useUserEmail} />
                            </Form.Item>
                            <Form.Item label="Password" name="password">
                                <Input.Password disabled={useUserEmail} />
                            </Form.Item>
                            <Form.Item label="Use TLS" name="use_tls" valuePropName="checked">
                                <Switch defaultChecked={user_setting?.use_tls} disabled={useUserEmail} />
                            </Form.Item>
                            <Form.Item label="Use SSL" name="use_ssl" valuePropName="checked">
                                <Switch defaultChecked={user_setting?.use_ssl} disabled={useUserEmail} />
                            </Form.Item>
                            <Divider />
                            <Form.Item wrapperCol={{ span: 24 }}>
                                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                    <Button type="primary" htmlType="submit">
                                        Save SMTP Settings
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                {/* Default Role Settings Card */}
                <Col xs={24} lg={12}>
                    <Card
                        title={<Title level={4} style={{ margin: 0 }}>Default Role Settings</Title>}
                        bordered={false}
                        style={{ height: '100%' }}
                    >
                        <Form
                            labelCol={{ span: 10 }}
                            wrapperCol={{ span: 14 }}
                            layout="horizontal"
                            onFinish={handleRoleSettings}
                            form={roleForm}
                        >
                            <Form.Item
                                label="Default Employee Role"
                                name="default_employee_role"
                                help="Select a role and save, then apply to all employees"
                            >
                                <Select
                                    placeholder="Select role"
                                    allowClear
                                    loading={loadingRoles}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {roles.map(role => (
                                        <Option key={role.id} value={role.id}>{role.role_name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item wrapperCol={{ span: 24 }}>
                                <Button
                                    type="dashed"
                                    onClick={handleApplyDefaultEmployeeRole}
                                    loading={applyingRole}
                                    disabled={!organizationSetting || !organizationSetting.default_employee_role}
                                    block
                                >
                                    Apply to All Employees
                                </Button>
                            </Form.Item>

                            <Divider style={{ margin: '16px 0' }} />

                            <Form.Item label="Default HR Role" name="default_hr_role">
                                <Select
                                    placeholder="Select role"
                                    allowClear
                                    loading={loadingRoles}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {roles.map(role => (
                                        <Option key={role.id} value={role.id}>{role.role_name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Default Accounts Role" name="default_accounts_role">
                                <Select
                                    placeholder="Select role"
                                    allowClear
                                    loading={loadingRoles}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {roles.map(role => (
                                        <Option key={role.id} value={role.id}>{role.role_name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Default Admin Role" name="default_admin_role">
                                <Select
                                    placeholder="Select role"
                                    allowClear
                                    loading={loadingRoles}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {roles.map(role => (
                                        <Option key={role.id} value={role.id}>{role.role_name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Default Sales Role" name="default_sales_role">
                                <Select
                                    placeholder="Select role"
                                    allowClear
                                    loading={loadingRoles}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {roles.map(role => (
                                        <Option key={role.id} value={role.id}>{role.role_name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Divider />
                            <Form.Item wrapperCol={{ span: 24 }}>
                                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                    <Button type="primary" htmlType="submit" loading={savingRoles}>
                                        Save Role Settings
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                {/* Payroll & Financial Year Settings Card */}
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <CalendarOutlined />
                                <Title level={4} style={{ margin: 0 }}>Payroll & Financial Year Settings</Title>
                            </div>
                        }
                        bordered={false}
                        style={{ height: '100%' }}
                    >
                        <Form
                            labelCol={{ span: 10 }}
                            wrapperCol={{ span: 14 }}
                            layout="horizontal"
                            onFinish={handlePayrollSettings}
                            form={payrollForm}
                            initialValues={{ payroll_start_day: 1, fy_start_month: 4, fy_start_day: 1 }}
                        >
                            <Title level={5} style={{ marginBottom: 16 }}>Payroll Period</Title>

                            <Form.Item
                                label="Payroll Start Day"
                                name="payroll_start_day"
                                rules={[{ required: true, message: 'Please select payroll start day' }]}
                                help="Day of month when payroll period starts (1-28)"
                            >
                                <Select placeholder="Select day">
                                    {[...Array(28)].map((_, i) => (
                                        <Option key={i + 1} value={i + 1}>{i + 1}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Payroll End Day"
                                help="Automatically calculated based on start day"
                            >
                                <Text strong style={{ fontSize: 16 }}>
                                    {getPayrollEndDay(payrollForm.getFieldValue('payroll_start_day') || organizationSetting?.payroll_start_day || 1)}
                                </Text>
                            </Form.Item>

                            <Divider style={{ margin: '16px 0' }} />

                            <Title level={5} style={{ marginBottom: 16 }}>Financial Year</Title>

                            <Form.Item
                                label="FY Start Month"
                                name="fy_start_month"
                                rules={[{ required: true, message: 'Please select FY start month' }]}
                                help="Month when financial year starts"
                            >
                                <Select placeholder="Select month">
                                    <Option value={1}>January</Option>
                                    <Option value={2}>February</Option>
                                    <Option value={3}>March</Option>
                                    <Option value={4}>April</Option>
                                    <Option value={5}>May</Option>
                                    <Option value={6}>June</Option>
                                    <Option value={7}>July</Option>
                                    <Option value={8}>August</Option>
                                    <Option value={9}>September</Option>
                                    <Option value={10}>October</Option>
                                    <Option value={11}>November</Option>
                                    <Option value={12}>December</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="FY Start Day"
                                name="fy_start_day"
                                rules={[{ required: true, message: 'Please select FY start day' }]}
                                help="Day when financial year starts (usually 1)"
                            >
                                <Select placeholder="Select day">
                                    {[...Array(28)].map((_, i) => (
                                        <Option key={i + 1} value={i + 1}>{i + 1}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="FY End Date"
                                help="Automatically calculated"
                            >
                                <Text strong style={{ fontSize: 16 }}>
                                    {getFYEndDate(
                                        payrollForm.getFieldValue('fy_start_month') || organizationSetting?.fy_start_month || 4,
                                        payrollForm.getFieldValue('fy_start_day') || organizationSetting?.fy_start_day || 1
                                    )}
                                </Text>
                            </Form.Item>

                            <Divider style={{ margin: '16px 0' }} />

                            <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#e6f7ff', borderRadius: 6, border: '1px solid #91d5ff' }}>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    <strong>Default (India):</strong> FY starts <strong>1 April</strong> and ends <strong>31 March</strong>
                                    <br />
                                    Example: FY 2025-26 runs from <strong>1 April 2025</strong> to <strong>31 March 2026</strong>
                                </Text>
                            </div>

                            <Form.Item wrapperCol={{ span: 24 }}>
                                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                    <Button type="primary" htmlType="submit" loading={savingPayroll}>
                                        Save Settings
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                {/* Accounting Settings Card */}
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <BankOutlined />
                                <Title level={4} style={{ margin: 0 }}>Accounting Settings</Title>
                            </div>
                        }
                        bordered={false}
                        style={{ height: '100%' }}
                    >
                        <Form
                            labelCol={{ span: 12 }}
                            wrapperCol={{ span: 12 }}
                            layout="horizontal"
                            onFinish={handleAccountingSettings}
                            form={accountingForm}
                            initialValues={{ exchange_rate_source: 'RBI' }}
                        >
                            <Form.Item
                                label="Exchange Rate Source"
                                name="exchange_rate_source"
                                help="Used by the fetch-rate button on invoices and quotes. RBI publishes only USD, GBP, EUR, JPY, AED and IDR; other currencies fall back to ExchangeRate-API."
                            >
                                <Select
                                    options={[
                                        { value: 'RBI', label: 'RBI Reference Rate' },
                                        { value: 'EXCHANGERATE_API', label: 'ExchangeRate-API' },
                                    ]}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Bank Transactions Locked Date"
                                name="bank_transactions_locked_date"
                                help="Transactions on or before this date cannot be added or imported"
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD-MM-YYYY"
                                    placeholder="Select locked date"
                                    allowClear
                                />
                            </Form.Item>

                            <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#fff1f0', borderRadius: 6, border: '1px solid #ffa39e' }}>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    <strong>Note:</strong> When a locked date is set, bank statement imports and manual transaction entries
                                    with dates on or before this date will be rejected. This helps protect historical financial data.
                                </Text>
                            </div>

                            <Form.Item wrapperCol={{ span: 24 }}>
                                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                    <Button type="primary" htmlType="submit" loading={savingAccounting}>
                                        Save Accounting Settings
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                {/* Invoice & Quote Default Terms Card */}
                <Col xs={24}>
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FileTextOutlined />
                                <Title level={4} style={{ margin: 0 }}>Default Invoice & Quote Terms</Title>
                            </div>
                        }
                        bordered={false}
                    >
                        <Form
                            layout="vertical"
                            onFinish={handleTermsSettings}
                            form={termsForm}
                        >
                            <Row gutter={24}>
                                <Col xs={24} lg={12}>
                                    <Form.Item
                                        label={<Text strong>Default Invoice Terms</Text>}
                                        name="invoice_terms"
                                        help="These terms will be automatically added to new invoices"
                                        getValueFromEvent={(event) => {
                                            const data = event.editor.getData();
                                            return data;
                                        }}
                                    >
                                        <CKEditor
                                            key={`invoice-terms-${organizationSetting?.id || 'new'}`}
                                            initData={organizationSetting?.invoice_terms || ''}
                                            config={policyEditorConfig}
                                            editorUrl={process.env.REACT_APP_BASE_URL + "/staticfiles/ckeditor/4.19.0/full-all/ckeditor.js"}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Form.Item
                                        label={<Text strong>Default Quote Terms</Text>}
                                        name="quote_terms"
                                        help="These terms will be automatically added to new quotes"
                                        getValueFromEvent={(event) => {
                                            const data = event.editor.getData();
                                            return data;
                                        }}
                                    >
                                        <CKEditor
                                            key={`quote-terms-${organizationSetting?.id || 'new'}`}
                                            initData={organizationSetting?.quote_terms || ''}
                                            config={policyEditorConfig}
                                            editorUrl={process.env.REACT_APP_BASE_URL + "/staticfiles/ckeditor/4.19.0/full-all/ckeditor.js"}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#fff7e6', borderRadius: 6, border: '1px solid #ffd591' }}>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    <strong>Note:</strong> These default terms will be pre-filled when creating new invoices or quotes.
                                    Users can still modify the terms on individual documents.
                                </Text>
                            </div>

                            <Form.Item>
                                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                    <Button type="primary" htmlType="submit" loading={savingTerms}>
                                        Save Default Terms
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                {/* User Session Management Card - Only for Admin */}
                {(user?.is_staff || user?.is_superuser) && (
                    <Col xs={24}>
                        <Card
                            title={<Title level={4} style={{ margin: 0 }}>User Session Management</Title>}
                            bordered={false}
                        >
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <div>
                                    <Title level={5} style={{ marginBottom: 8 }}>Logout All Users</Title>
                                    <Text type="secondary">
                                        Terminate all active sessions for non-admin users. This action will force all regular users
                                        to log in again. Admin users (staff and superusers) will remain logged in.
                                    </Text>
                                </div>
                                <Button
                                    danger
                                    type="primary"
                                    icon={<LogoutOutlined />}
                                    onClick={handleLogoutAllUsers}
                                    loading={loggingOutAllUsers}
                                    size="large"
                                >
                                    Logout All Non-Admin Users
                                </Button>
                            </Space>
                        </Card>
                    </Col>
                )}
            </Row>
        </div>
    )

}

export default Settings;